import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CalendarMonth, Visibility, Person } from '@mui/icons-material';

function InterviewPanel() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Scheduling state
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [interviewType, setInterviewType] = useState('technical');
  const [scheduledDate, setScheduledDate] = useState('');
  const [meetingLink, setMeetingLink] = useState('');
  const [interviewMode, setInterviewMode] = useState('Virtual');
  const [codingTestType, setCodingTestType] = useState('AI-Generated');
  const [level, setLevel] = useState("fresher");
  const [manualQuestion, setManualQuestion] = useState({
    question: '',
    testCases: [],
    timeLimit: 30,
    maxScore: 100
  });
  const [questions, setQuestions] = useState([""]);
  const [manualInterviewQuestions, setManualInterviewQuestions] = useState('');
  const [notes, setNotes] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    fetchshortlistedApplications();
  }, []);

  const fetchshortlistedApplications = async () => {
    try {
      const response = await axios.get('/api/admin/applications');
      // Filter for shortlisted only
      const shortlisted = (response.data.applications || []).filter(
        app => app.status === 'shortlisted'
      );
      setApplications(shortlisted);
    } catch (err) {
      console.error('Failed to fetch applications:', err);
      setError('Failed to load shortlisted applications.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSchedule = async (app, type) => {
    setSelectedApp(app);
    setInterviewType(type);
    setScheduleDialogOpen(true);
    setScheduledDate('');
    setMeetingLink('');
    setInterviewMode('Virtual');
    setCodingTestType('AI-Generated');
    setManualQuestion({
      question: '',
      testCases: [],
      timeLimit: 30,
      maxScore: 100
    });
    setManualInterviewQuestions('');
    setNotes('');

    if (type === 'coding') {
      try {
        const response = await axios.get(`/api/jobs/${app.jobId || app.job_id}`);
        if (response.data.codingTestConfig) {
          setManualQuestion({
            question: response.data.codingTestConfig.question || '',
            testCases: response.data.codingTestConfig.testCases || [],
            timeLimit: response.data.codingTestConfig.timeLimit || 30,
            maxScore: response.data.codingTestConfig.maxScore || 100
          });
        }
      } catch (err) {
        console.error('Failed to fetch job questions:', err);
      }
    }
  };

  const handleScheduleInterview = async () => {
    try {
      let endpoint = '';
      let payload = { 
        scheduledAt: new Date(scheduledDate).toISOString(),
        meetingLink: (interviewType !== 'coding' && interviewMode === 'Virtual') ? meetingLink : '',
        interviewMode,
        notes
      };

      if (interviewType === 'technical') {
        endpoint = `/api/technical-interview/${selectedApp.id}/schedule`;
        if (manualInterviewQuestions) {
          payload.questions = manualInterviewQuestions.split('\n').filter(q => q.trim() !== '');
        }
      } else if (interviewType === 'hr') {
        endpoint = `/api/hr-interview/${selectedApp.id}/schedule`;
        if (manualInterviewQuestions) {
          payload.questions = manualInterviewQuestions.split('\n').filter(q => q.trim() !== '');
        }
      } else if (interviewType === 'coding') {
        endpoint = `/api/coding-test/${selectedApp.id}/schedule`;
        payload.codingTestType = codingTestType;
        payload.level = level;
        if (codingTestType === 'AI-Manual') {
          const validQuestions = questions.filter(q => q.trim() !== "");

          payload.coding_questions = validQuestions;          // coding question
          payload.manualQuestions = validQuestions.slice(1); // rest = manual questions
          payload.testCases = manualQuestion.testCases;
          payload.timeLimit = manualQuestion.timeLimit;
          payload.maxScore = manualQuestion.maxScore;
        }
      }
      
      await axios.patch(endpoint, payload);
      
      const typeLabel = interviewType === 'coding' ? 'Coding Test' : (interviewType === 'technical' ? 'Technical' : 'HR');
      setSuccess(`${typeLabel} scheduled for ${selectedApp.first_name} ${selectedApp.last_name}`);
      setScheduleDialogOpen(false);
      setScheduledDate('');
      setMeetingLink('');
      setNotes('');
      // Refresh data to show updated stage/status if needed
      fetchshortlistedApplications();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to schedule interview');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4"
                    sx={{
                      color: "primary.main", // Premium Accent color
                      fontWeight: "bold",
                      mb: 3
                    }}
        >
          Interview Panel
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Manage and schedule interviews for shortlisted candidates
        </Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={1}>
        {applications.map((app) => (
          <Grid item xs={12} md={6} lg={4} key={app.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Person color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    {app.first_name} {app.last_name}
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {app.email}
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Job:</strong> {app.job_title}
                </Typography>

                <Box sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Overall Status</Typography>
                  <Box>
                    <Chip 
                      label={app.status} 
                      color="success" 
                      size="small" 
                    />
                  </Box>
                </Box>

                <Box sx={{ mt: 1, mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Current Status</Typography>
                  <Box>
                    <Chip 
                      label={app.current_stage || app.currentStage} 
                      color="info" 
                      size="small" 
                      variant="outlined"
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>Quick Actions</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CalendarMonth />}
                    onClick={() => handleOpenSchedule(app, 'coding')}
                    fullWidth
                    disabled={app.status !== 'shortlisted' && app.current_stage !== 'application' && app.current_stage !== 'coding_test'}
                  >
                    Schedule Coding Test
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CalendarMonth />}
                    onClick={() => handleOpenSchedule(app, 'technical')}
                    fullWidth
                    disabled={app.current_stage === 'hr_interview' || app.current_stage === 'offer' || app.current_stage === 'completed'}
                  >
                    Schedule Tech Interview
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CalendarMonth />}
                    onClick={() => handleOpenSchedule(app, 'hr')}
                    fullWidth
                    disabled={app.current_stage === 'coding_test' || app.current_stage === 'technical_interview' || app.current_stage === 'application'}
                  >
                    Schedule HR Interview
                  </Button>
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                <Button
                  size="small"
                  startIcon={<Visibility />}
                  onClick={() => navigate(`/admin/applications/${app.id}`)}
                >
                  View Application
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {applications.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">
            No shortlisted candidates found at the moment.
          </Typography>
        </Box>
      )}

      {/* Schedule Interview Dialog */}
      <Dialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {interviewType === 'coding' ? 'Schedule Coding Test' : 
           interviewType === 'technical' ? 'Schedule Technical Interview' : 
           'Schedule HR Interview'}
        </DialogTitle>
        <DialogContent sx={{ maxHeight: "70vh", overflowY: "auto" }}>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2">
              Candidate: <strong>{selectedApp?.first_name} {selectedApp?.last_name}</strong>
            </Typography>
            <TextField
              label="Interview Date and Time"
              type="datetime-local"
              fullWidth
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              required
            />
            <Typography color="red">DEBUG: CODING SECTION</Typography>
            {interviewType === 'coding' && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Coding Process Type</InputLabel>
                  <Select
                    value={codingTestType}
                    label="Coding Process Type"
                    onChange={(e) => setCodingTestType(e.target.value)}
                  >
                    <MenuItem value="AI-Generated">AI-Generated Interview</MenuItem>
                    <MenuItem value="AI-Manual">AI with Manual questions generated Interview</MenuItem>
                  </Select>
                </FormControl>


                <FormControl fullWidth>
                  <InputLabel>Interview Level</InputLabel>
                  <Select
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                  >
                    <MenuItem value="fresher">Fresher</MenuItem>
                    <MenuItem value="mid">Mid</MenuItem>
                    <MenuItem value="experienced">Experienced</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            {interviewType === 'coding' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {questions.map((q, index) => (
                  <TextField
                    key={index}
                    label={`Question ${index + 1}`}
                    value={q}
                    onChange={(e) => {
                      const updated = [...questions];
                      updated[index] = e.target.value;
                      setQuestions(updated);
                    }}
                    fullWidth
                  />
                ))}

                <Button
                  variant="outlined"
                  onClick={() => setQuestions([...questions, ""])}
                >
                  + Add Question
                </Button>
              </Box>
            )}

            {interviewType !== 'coding' && (
              <TextField
                label="Custom Interview Questions"
                placeholder="Enter each question on a new line. These will be added to the AI-generated questions."
                multiline
                rows={4}
                fullWidth
                value={manualInterviewQuestions}
                onChange={(e) => setManualInterviewQuestions(e.target.value)}
                helperText="Optional: Add specific questions you want the AI to ask during this interview."
              />
            )}
            {interviewType !== 'coding' && (
              <FormControl fullWidth>
                <InputLabel>Interview Mode</InputLabel>
                <Select
                  value={interviewMode}
                  label="Interview Mode"
                  onChange={(e) => setInterviewMode(e.target.value)}
                >
                  <MenuItem value="Virtual">Virtual Mode</MenuItem>
                  <MenuItem value="Face-Face">Face-to-Face</MenuItem>
                </Select>
              </FormControl>
            )}
            {interviewType !== 'coding' && interviewMode === 'Virtual' && (
              <TextField
                label="Meeting Link"
                placeholder="e.g. https://zoom.us/j/..."
                fullWidth
                value={meetingLink}
                onChange={(e) => setMeetingLink(e.target.value)}
                required={interviewMode === 'Virtual'}
                error={interviewMode === 'Virtual' && !meetingLink}
                helperText={interviewMode === 'Virtual' && !meetingLink ? "Meeting link is mandatory for virtual mode" : ""}
              />
            )}
            <TextField
              label="Notes"
              placeholder="Any additional instructions for the candidate..."
              multiline
              rows={4}
              fullWidth
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleScheduleInterview} 
            variant="contained" 
            disabled={
              !scheduledDate || 
              (interviewType !== 'coding' && interviewMode === 'Virtual' && !meetingLink) ||
              (interviewType === 'coding' && codingTestType === 'AI-Manual' && questions.filter(q => q.trim() !== "").length === 0)
            }
          >
            Schedule
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default InterviewPanel;
